import { useConnector } from 'react-instantsearch';
import type { SearchParameters, SearchResults } from 'algoliasearch-helper';
import type { Connector } from 'instantsearch.js';
import { sortRecordsByLabel } from '@instant_search/carwash/utils';

type ConnectorParams = {
  attribute: string;
  relatedAttributes: string | string[];
};

type ConnectorState = {
  refine?(value: string | string[]): void;
  sendEvent?(eventType: string, facetValue: string, eventName?: string): void;
};

type RenderState = {
  items: SearchResults.FacetValue[];
  refine(value: string | string[]): void;
};

type WidgetDescription = {
  $$type: 'arnoldclark.RelatedRefinementList';
  indexRenderState: {
    relatedRefinementList: {
      [attribute: string]: RenderState;
    };
  };
  indexUiState: {
    relatedRefinementList: {
      [attribute: string]: string[];
    };
  };
  renderState: RenderState;
};

type RelatedRefinementListConnector = Connector<
  WidgetDescription,
  ConnectorParams
>;

const connectRelatedRefinementList: RelatedRefinementListConnector = (
  renderFn,
  unmountFn = () => {}
) => {
  return function relatedRefinementList(widgetParams) {
    const { attribute, relatedAttributes } = widgetParams;

    const connectorState: ConnectorState = {};

    return {
      $$type: 'arnoldclark.RelatedRefinementList',
      getWidgetRenderState({ results, helper, instantSearchInstance }) {
        // To ensure `sendEvent` keeps the same reference across renders, create
        // and store it once outside the method scope.
        if (!connectorState.sendEvent) {
          connectorState.sendEvent = (
            eventType,
            facetValue,
            eventName = 'Filter Applied'
          ) => {
            if (
              helper.state.disjunctiveFacetsRefinements[attribute].includes(
                facetValue
              )
            ) {
              instantSearchInstance.sendEventToInsights({
                insightsMethod: 'clickedFilters',
                widgetType: this.$$type,
                eventType,
                payload: {
                  eventName,
                  index: helper.getIndex(),
                  filters: [`${attribute}:${facetValue}`],
                },
                attribute,
              });
            }
          };
        }

        if (!connectorState.refine) {
          connectorState.refine = (toggledValue) => {
            function handleToggle(toggledValue: string) {
              helper.toggleFacetRefinement(attribute, toggledValue);

              const facetRemoved =
                helper
                  .getRefinements(attribute)
                  .find(({ value }) => value === toggledValue) === undefined;

              function removeRelatedFacets(attribute: string): void {
                const selectedRelated = helper
                  .getRefinements(attribute)
                  .filter(({ value }) => value.includes(toggledValue));

                selectedRelated.forEach((relatedFacet) =>
                  helper.toggleFacetRefinement(attribute, relatedFacet.value)
                );
              }

              if (facetRemoved) {
                Array.isArray(relatedAttributes)
                  ? relatedAttributes.forEach((relatedAttribute) =>
                      removeRelatedFacets(relatedAttribute)
                    )
                  : removeRelatedFacets(relatedAttributes);
              }

              connectorState.sendEvent!('click', toggledValue);
            }

            if (Array.isArray(toggledValue)) {
              toggledValue.forEach(handleToggle);
            } else {
              handleToggle(toggledValue);
            }

            return helper.search();
          };
        }

        if (!results) {
          return { items: [], refine: connectorState.refine, widgetParams };
        }

        const items =
          (results.getFacetValues(attribute, {
            sortBy: ['name:asc, count:desc'],
          }) as SearchResults.FacetValue[]) || [];

        return {
          items: sortRecordsByLabel(items),
          refine: connectorState.refine,
          widgetParams,
        };
      },
      getRenderState(renderState, renderOptions) {
        // The global render state is merged with a new one to store the render
        // state of the current widget.
        return {
          ...renderState,
          relatedRefinementList: {
            ...renderState.relatedRefinementList,
            [attribute]: this.getWidgetRenderState(renderOptions),
          },
        };
      },
      init(initOptions) {
        const { instantSearchInstance } = initOptions;

        renderFn(
          // The render state is the data provided to the render function,
          // necessary to build the UI.
          {
            ...this.getWidgetRenderState(initOptions),
            instantSearchInstance,
          },
          // Calling the function with `isFirstRender=true` lets you perform
          // conditional logic in the render function.
          true
        );
      },
      render(renderOptions) {
        const { instantSearchInstance } = renderOptions;

        renderFn(
          // The render state is the data provided to the render function,
          // necessary to build the UI.
          {
            ...this.getWidgetRenderState(renderOptions),
            instantSearchInstance,
          },
          // Calling the function with `isFirstRender=false` lets you perform
          // conditional logic in the render function.
          false
        );
      },
      dispose() {
        unmountFn();
      },
      getWidgetUiState(uiState, { searchParameters }) {
        // The global UI state is merged with a new one to store the UI
        // state of the current widget.
        return {
          ...uiState,
          relatedRefinementList: {
            ...uiState.relatedRefinementList,
            // You can use multiple `relatedRefinementList` widgets in a single
            // app so you need to register each of them separately.
            // Each `relatedRefinementList` widget's UI state is stored by
            // the `attribute` it impacts.
            [attribute]: searchParameters.getDisjunctiveRefinements(attribute),
          },
        };
      },
      getWidgetSearchParameters(searchParameters, { uiState }) {
        const state = searchParameters.addDisjunctiveFacet(attribute);
        const values = uiState.relatedRefinementList?.[attribute];

        if (Array.isArray(values)) {
          return values.reduce<SearchParameters>(
            (acc, curr) => acc.addDisjunctiveFacetRefinement(attribute, curr),
            state
          );
        }

        return state;
      },
    };
  };
};

export default function useRelatedRefinementList(props: ConnectorParams) {
  return useConnector(connectRelatedRefinementList, props);
}
